import React, { useCallback, useMemo, useState } from 'react';
import kkpService from '../../services/kkpService';
import { notify } from '../../components/common/AppToast';
import { formatPlanningImportMessage } from '../../utils/kkpPlanningImport';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpPlanningStatutDisplay from '../../components/kkp/KkpPlanningStatutDisplay';
import KkpVenteSetModal from '../../components/kkp/KkpVenteSetModal';
import KkpPlanningMagInlineCell from '../../components/kkp/KkpPlanningMagInlineCell';
import { filterStatutsForContext, filterStatutsForMonth } from '../../utils/kkpStatutUtils';
import KkpPlanningStickyTableWrap from '../../components/kkp/KkpPlanningStickyTableWrap';
import { AppFilterSelect, AppFilterCommercialSelect, AppFilterField } from '../../components/filters';
import { AppAlert, AppButton } from '../../components/ui';
import KkpPlanningImportModal from '../../components/kkp/KkpPlanningImportModal';
import KkpPublicationLinkButton from '../../components/kkp/KkpPublicationLinkButton';
import KkpPubImageButton from '../../components/kkp/KkpPubImageButton';
import KkpBatValideModal from '../../components/kkp/KkpBatValideModal';
import SortableTh from '../../components/common/SortableTh';
import KkpClientAutocomplete from '../../components/kkp/KkpClientAutocomplete';
import { getPlanningRowKey, mergePlanningRow } from '../../utils/kkpPlanningMergeUtils';
import { confirmVenteClear } from '../../utils/confirmVenteDelete';
import {
  buildClientInlinePayload,
  clientInlineDraftEquals,
  getClientInlineDraft,
} from '../../utils/kkpPlanningClientInline';
import { suggestBriefWizardStep, briefStatusWizardStep } from '../../utils/kkpVenteBriefUtils';
import { filterRowsByMulti, joinOrEmpty, singleOrEmpty, firstOrFallback, buildYearFilterOptions } from '../../utils/kkpPlanningFilters';
import {
  normalizeMultiFilter,
  normalizeTextFilter,
} from '../../utils/kkpPlanningFiltersStorage';
import {
  getPlanningClientDisplay,
  getPlanningViewEmptyMessage,
  isPlanningIndispoWithoutClient,
  PLANNING_VIEW_CLIENTS,
} from '../../utils/kkpPlanningViewMode';
import { resolveCommercialSelectionFromDolibarrClient } from '../../utils/kkpDolibarrCommercialPrefill';
import { useKkpPublications } from '../../hooks/useKkpPublications';
import usePlanningVentesPage from '../../hooks/usePlanningVentesPage';
import KkpPlanningPageShell, { KkpPlanningEmptyRow } from '../../components/kkp/KkpPlanningPageShell';
import { importPeriodLabelFromYearMonth } from '../../utils/kkpPlanningFormat';

const buildSetFilters = (defaultPeriod, commercialId = '') => ({
  annee: [String(defaultPeriod?.annee ?? new Date().getFullYear())],
  mois: [String(defaultPeriod?.mois ?? new Date().getMonth() + 1)],
  commercial_id: commercialId ? [String(commercialId)] : [],
  format: [],
  statut_phase: [],
  search: '',
});

const SET_FILTERS_STORAGE_KEY = 'kkp_planning_set_filters';

const normalizeSetFilters = (savedFilters, defaultPeriod) => {
  const defaults = buildSetFilters(defaultPeriod, '');
  const source = savedFilters && typeof savedFilters === 'object' ? savedFilters : {};

  return {
    // Période métier (mois en cours, ou suivant si CDF validé / butoir dépassée).
    annee: defaults.annee,
    mois: defaults.mois,
    commercial_id: normalizeMultiFilter(source.commercial_id),
    format: normalizeMultiFilter(source.format),
    statut_phase: normalizeMultiFilter(source.statut_phase),
    search: normalizeTextFilter(source.search, ''),
  };
};

const toSetApiFilters = (filters) => ({
  annee: joinOrEmpty(filters.annee),
  mois: joinOrEmpty(filters.mois),
  commercial_id: joinOrEmpty(filters.commercial_id),
  format: joinOrEmpty(filters.format),
  statut_phase: joinOrEmpty(filters.statut_phase),
  search: filters.search ?? '',
});

const applySetClientFilters = (rows, currentFilters) => {
  let next = rows;
  next = filterRowsByMulti(next, currentFilters?.annee, (row) => row.annee);
  next = filterRowsByMulti(next, currentFilters?.mois, (row) => row.mois);
  next = filterRowsByMulti(next, currentFilters?.commercial_id, (row) => row.commercial_id);
  next = filterRowsByMulti(next, currentFilters?.format, (row) => row.format);
  next = filterRowsByMulti(next, currentFilters?.statut_phase, (row) => row.statut_phase);
  return next;
};

const TABLE_SORT_COLUMNS = {
  periode: { type: 'number', getValue: (row) => (row.annee ?? 0) * 100 + (row.mois ?? 0) },
  commercial: { type: 'string', getValue: (row) => row.commercial_nom },
  client: { type: 'string', getValue: (row) => getPlanningClientDisplay(row, '') },
  format: { type: 'string', getValue: (row) => row.format },
  statut: { type: 'string', getValue: (row) => row.statut_phase },
  commentaire: { type: 'string', getValue: (row) => row.commentaire },
};

const KkpPlanningSetPage = () => {
  const [importResult, setImportResult] = useState(null);
  const { getPublicationUrl } = useKkpPublications();

  const fetchMeta = useCallback(() => kkpService.getSetMeta(), []);
  const initFiltersFromMeta = useCallback(
    (meta, savedFilters) => normalizeSetFilters(savedFilters, meta.default_period),
    [],
  );
  const fetchVentes = useCallback(async (filters, { page }) => {
    const apiFilters = toSetApiFilters(filters);
    const gridMode = Boolean(singleOrEmpty(filters.annee))
      && !apiFilters.search
      && !apiFilters.commercial_id
      && !apiFilters.statut_phase
      && (filters.commercial_id?.length ?? 0) <= 1
      && (filters.statut_phase?.length ?? 0) <= 1;
    const limit = gridMode && !apiFilters.mois && (filters.mois?.length ?? 0) === 0 ? 500 : 50;
    const result = await kkpService.listVentesSet({ ...apiFilters, page, limit });
    return {
      ventes: applySetClientFilters(result.ventes, filters),
      pagination: result.pagination,
    };
  }, []);

  const {
    ask,
    confirmModal,
    meta,
    ventes,
    setVentes,
    commerciaux,
    defaultCommercialId,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    setError,
    showModal,
    editingVente,
    modalInitialValues,
    modalWizardStep,
    openCreate,
    openEdit,
    closeModal,
    setImporting,
    setExporting,
    showImportModal,
    setShowImportModal,
    importStrategy,
    setImportStrategy,
    wipeScope,
    setWipeScope,
    fileInputRef,
    editingCell,
    setEditingCell,
    editDraft,
    setEditDraft,
    inlineSaving,
    setInlineSaving,
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
    statutSelectProps,
    viewMode,
    setViewMode,
    changeViewMode,
    viewModeCounts,
    visibleVentes,
    sortedRows,
    sort,
    toggleSort,
    getSortIcon,
    commercialOptions,
    loadVentes,
    findVenteByRowId,
    getRowId,
    buildImportExportMenu,
  } = usePlanningVentesPage({
    storageKey: SET_FILTERS_STORAGE_KEY,
    tableSortColumns: TABLE_SORT_COLUMNS,
    fetchMeta,
    initFiltersFromMeta,
    fetchVentes,
    filterRows: applySetClientFilters,
  });

  const anneeFilterSingle = singleOrEmpty(filters?.annee);
  const moisFilterSingle = singleOrEmpty(filters?.mois);
  const filterStatuts = useMemo(
    () => filterStatutsForContext(
      meta?.statuts,
      anneeFilterSingle ? Number(anneeFilterSingle) : null,
      moisFilterSingle ? Number(moisFilterSingle) : null,
    ),
    [meta?.statuts, anneeFilterSingle, moisFilterSingle],
  );

  const anneeOptions = useMemo(
    () => buildYearFilterOptions(filters?.annee),
    [filters?.annee],
  );
  const moisOptions = useMemo(
    () => (meta?.mois ?? []).map((m) => ({ value: String(m.value), label: m.label })),
    [meta?.mois],
  );
  const formatOptions = useMemo(
    () => (meta?.formats ?? []).map((f) => ({ value: f.value, label: f.value })),
    [meta?.formats],
  );
  const statutOptions = useMemo(
    () => filterStatuts.map((s) => ({ value: s.value, label: s.value })),
    [filterStatuts],
  );

  const handleOpenCreate = () => {
    openCreate({
      annee: Number(firstOrFallback(filters?.annee, new Date().getFullYear())),
      mois: Number(firstOrFallback(filters?.mois, new Date().getMonth() + 1)),
    });
  };

  const handleCreaPubChange = (venteId, pieces) => {
    setVentes((prev) => prev.map((row) => (
      row.id === venteId ? { ...row, crea_pub_pieces_jointes: pieces } : row
    )));
  };

  const handleOpenEdit = (vente, wizardStep = undefined) => {
    openEdit(vente, typeof wizardStep === 'number' ? wizardStep : suggestBriefWizardStep(vente));
  };

  const resetFilters = () => {
    setViewMode(PLANNING_VIEW_CLIENTS);
    setFilters(buildSetFilters(meta?.default_period, ''));
  };

  const handleClear = async (venteOrId) => {
    const vente = typeof venteOrId === 'object'
      ? venteOrId
      : findVenteByRowId(venteOrId) ?? ventes.find((v) => v.id === venteOrId);
    const id = vente?.id ?? venteOrId;
    if (!id) return;
    if (!(await confirmVenteClear(ask, vente?.client_label))) return;
    try {
      await kkpService.clearVenteSet(id);
      loadVentes(pagination.page || 1);
    } catch (err) {
      setError(err.message);
    }
  };
  const handleDelete = handleClear;

  const getInlineDraft = (vente, field) => {
    switch (field) {
      case 'periode':
        return { annee: vente.annee, mois: vente.mois };
      case 'commercial_id':
        return vente.commercial_id != null ? String(vente.commercial_id) : '';
      case 'client_label':
        return getClientInlineDraft(vente);
      case 'format':
        return vente.format ?? '';
      case 'statut_phase':
        return vente.statut_phase ?? '';
      case 'commentaire':
        return vente.commentaire ?? '';
      default:
        return '';
    }
  };

  const startInlineEdit = async (venteId, field) => {
    if (!venteId || !field) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }
    if (editingCell && (editingCell.venteId !== venteId || editingCell.field !== field)) {
      await saveInlineEdit();
    }
    const vente = findVenteByRowId(venteId);
    if (!vente) return;
    setEditingCell({ venteId, field });
    setEditDraft(getInlineDraft(vente, field));
  };

  const buildInlinePayload = (field, draft) => {
    switch (field) {
      case 'periode':
        return { annee: draft.annee, mois: draft.mois };
      case 'commercial_id':
        return { commercial_id: draft || null };
      case 'client_label':
        return buildClientInlinePayload(draft);
      case 'format':
        return { format: draft };
      case 'statut_phase':
        return { statut_phase: draft ?? '' };
      case 'commentaire':
        return { commentaire: draft || null };
      default:
        return {};
    }
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const vente = findVenteByRowId(editingCell.venteId);
    if (!vente) return;

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const original = getInlineDraft(vente, editingCell.field);
    const unchanged = editingCell.field === 'client_label'
      ? clientInlineDraftEquals(draft, vente)
      : JSON.stringify(draft) === JSON.stringify(original);
    if (unchanged) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }

    setInlineSaving(true);
    setError(null);
    try {
      const payload = buildInlinePayload(editingCell.field, draft);

      if (vente.is_placeholder) {
        const createPayload = {
          annee: vente.annee,
          mois: vente.mois,
          format: vente.format,
          statut_phase: 'Option',
          client_label: '',
          ...payload,
        };
        const created = await kkpService.createVenteSet(createPayload);
        setVentes((prev) => mergePlanningRow(prev, created, { replaceKey: getRowId(vente) }));
      } else {
        const updated = await kkpService.updateVenteSet(vente.id, payload);
        setVentes((prev) => mergePlanningRow(prev, updated));
      }

      setEditingCell(null);
      setEditDraft(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleExport = async () => {
    if (!filters) return;
    setExporting(true);
    setError(null);
    try {
      await kkpService.exportVentesSet({
        annee: singleOrEmpty(filters.annee),
        mois: singleOrEmpty(filters.mois),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const importPeriodLabel = useMemo(
    () => importPeriodLabelFromYearMonth(
      singleOrEmpty(filters?.annee),
      singleOrEmpty(filters?.mois),
      moisOptions,
    ),
    [filters?.annee, filters?.mois, moisOptions],
  );

  const handleImportClick = () => {
    setImportStrategy('upsert');
    setWipeScope(singleOrEmpty(filters?.annee) && singleOrEmpty(filters?.mois) ? 'month' : 'all');
    setShowImportModal(true);
  };

  const handleImportConfirm = () => {
    if (importStrategy === 'wipe' && wipeScope === 'month' && (!singleOrEmpty(filters.annee) || !singleOrEmpty(filters.mois))) {
      setError('Sélectionnez une seule année et un seul mois dans les filtres pour remplacer une période.');
      return;
    }
    setShowImportModal(false);
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await kkpService.importVentesSet(file, {
        upsert: importStrategy === 'upsert',
        replace: importStrategy === 'wipe' && wipeScope === 'month',
        replaceAll: importStrategy === 'wipe' && wipeScope === 'all',
        annee: importStrategy === 'wipe' && wipeScope === 'month' ? singleOrEmpty(filters.annee) : '',
        mois: importStrategy === 'wipe' && wipeScope === 'month' ? singleOrEmpty(filters.mois) : '',
      });
      setImportResult(result);
      const msg = formatPlanningImportMessage(result);
      if ((result.errors?.length ?? 0) > 0) {
        notify.warning(`${msg} ${result.errors.length} erreur(s) — voir la console.`);
        console.warn('Import SET erreurs', result.errors);
      } else {
        notify.success(msg);
      }
      loadVentes(1);
    } catch (err) {
      setError(err.message);
      notify.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <KkpPlanningPageShell
      title="Planning ventes SET"
      subtitle="Suivi des ventes agenda / set de table par mois et commercial"
      menuItems={buildImportExportMenu(handleImportClick, handleExport)}
      error={error}
      banners={importResult ? (
        <AppAlert variant="success">
          Import terminé : {importResult.updated > 0 ? `${importResult.updated} mise(s) à jour, ` : ''}
          {importResult.created} créée(s)
          {importResult.skipped > 0 && `, ${importResult.skipped} ignorée(s)`}.
          {(importResult.errors?.length ?? 0) > 0 && (
            <ul className="mb-0 mt-2 small">
              {importResult.errors.map((err) => (
                <li key={`${err.row}-${err.message}`}>Ligne {err.row} : {err.message}</li>
              ))}
            </ul>
          )}
        </AppAlert>
      ) : null}
      fileInputRef={fileInputRef}
      onImportFile={handleImportFile}
      resetDisabled={!filters}
      onResetFilters={resetFilters}
      filterChildren={(
        <>
          <AppFilterSelect
            id="set-annee"
            label="Année"
            col="col-6 col-md-2"
            value={filters?.annee ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, annee: next }))}
            options={anneeOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="set-mois"
            label="Mois"
            col="col-6 col-md-2"
            value={filters?.mois ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, mois: next }))}
            options={moisOptions}
            disabled={!filters}
          />
          <AppFilterCommercialSelect
            id="set-commercial"
            col="col-6 col-md-2"
            value={filters?.commercial_id ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, commercial_id: next }))}
            options={commercialOptions}
            disabled={!filters}
            multiple
          />
          <AppFilterSelect
            id="set-format"
            label="Format"
            col="col-6 col-md-2"
            value={filters?.format ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, format: next }))}
            options={formatOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="set-statut"
            label="Statut"
            col="col-6 col-md-2"
            value={filters?.statut_phase ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, statut_phase: next }))}
            options={statutOptions}
            disabled={!filters}
          />
          <AppFilterField col="col-12 col-md-4" label="Client">
            <KkpClientAutocomplete
              value={filters?.search ?? ''}
              onChange={(val) => setFilters((f) => ({ ...f, search: val }))}
              onSelect={async (client) => {
                const label = String(client?.nom ?? client?.label ?? '').trim();
                try {
                  const matchedCommercial = await resolveCommercialSelectionFromDolibarrClient(client, commercialOptions);
                  setFilters((f) => ({
                    ...f,
                    search: label,
                    commercial_id: matchedCommercial ? [matchedCommercial.value] : [],
                  }));
                } catch {
                  setFilters((f) => ({ ...f, search: label }));
                }
              }}
              disabled={!filters}
              size="sm"
              placeholder="Client ou tiers Dolibarr…"
            />
          </AppFilterField>
        </>
      )}
      viewMode={viewMode}
      onViewModeChange={changeViewMode}
      viewModeCounts={viewModeCounts}
      onCreate={handleOpenCreate}
      loading={loading}
      table={(
        <KkpPlanningStickyTableWrap>
          <table className="table table-hover mb-0 kkp-planning-table kkp-planning-table--set">
            <thead>
              <tr>
                <SortableTh sortKey="periode" label="Période" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="client" label="Client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="format" label="Format" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commentaire" label="Commentaire" className="kkp-table-comment" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh label="Actions" className="text-end kkp-col-actions" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {visibleVentes.length === 0 ? (
                <KkpPlanningEmptyRow
                  colSpan={7}
                  message={getPlanningViewEmptyMessage(viewMode)}
                  actions={[
                    {
                      key: 'reset',
                      label: 'Réinitialiser',
                      icon: 'mdi:filter-off-outline',
                      onClick: resetFilters,
                    },
                    {
                      key: 'create',
                      label: 'Nouvelle vente',
                      icon: 'mdi:plus',
                      variant: 'primary',
                      onClick: handleOpenCreate,
                    },
                  ]}
                />
              ) : sortedRows.map((v) => (
                <tr key={getPlanningRowKey(v)} className={v.is_placeholder ? 'table-light' : undefined}>
                  <KkpPlanningMagInlineCell
                    venteId={getRowId(v)}
                    field="periode"
                    type="periode"
                    options={meta?.mois ?? []}
                    optionValueKey="value"
                    optionLabelKey="label"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    <span className="text-sm">{v.mois_label} {v.annee}</span>
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={getRowId(v)}
                    field="commercial_id"
                    type="select"
                    options={commerciaux}
                    optionValueKey="id"
                    optionLabelKey="nom"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    {v.commercial_nom ? (
                      <KkpColorBadge color={v.commercial_couleur} vivid>
                        {v.commercial_nom}
                      </KkpColorBadge>
                    ) : (
                      <span className="text-secondary-light">—</span>
                    )}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={getRowId(v)}
                    field="client_label"
                    type="client"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="fw-medium"
                  >
                    {v.is_placeholder && !v.client_label ? (
                      <span className="text-secondary-light fst-italic">Vide</span>
                    ) : (
                      <span className={isPlanningIndispoWithoutClient(v) ? 'text-secondary-light fst-italic' : undefined}>
                        {getPlanningClientDisplay(v)}
                      </span>
                    )}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={getRowId(v)}
                    field="format"
                    type="select"
                    allowEmpty={false}
                    options={meta?.formats ?? []}
                    optionValueKey="value"
                    optionLabelKey="value"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    <span className="text-sm">{v.format}</span>
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={getRowId(v)}
                    field="statut_phase"
                    type="select"
                    options={filterStatutsForMonth(meta?.statuts, v.annee, v.mois)}
                    optionValueKey="value"
                    optionLabelKey="value"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    {...statutSelectProps(v)}
                  >
                    {v.statut_phase ? (
                      <KkpPlanningStatutDisplay
                        vente={v}
                        onBriefClick={(briefStatus) => handleOpenEdit(v, briefStatusWizardStep(v, briefStatus))}
                      />
                    ) : v.is_placeholder ? (
                      <span className="text-secondary-light fst-italic">Vide</span>
                    ) : (
                      <span className="text-secondary-light">—</span>
                    )}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={getRowId(v)}
                    field="commentaire"
                    type="text"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-sm text-secondary-light kkp-table-comment"
                  >
                    {v.commentaire || '—'}
                  </KkpPlanningMagInlineCell>
                  <td className="text-end kkp-col-actions">
                    <div className="kkp-actions-toolbar">
                      {v.id ? (
                        <>
                          <KkpPublicationLinkButton
                            url={getPublicationUrl('set', v.annee, v.mois, v.publication_url)}
                            type="set"
                          />
                          <KkpPubImageButton vente={v} onCreaPubChange={handleCreaPubChange} />
                          <AppButton
                            size="sm"
                            variant="outline-primary"
                            icon="mdi:pencil"
                            title="Modifier"
                            aria-label="Modifier"
                            onClick={() => handleOpenEdit(v)}
                          />
                          <AppButton
                            size="sm"
                            variant="outline-warning"
                            icon="mdi:eraser"
                            title="Effacer le contenu (conserve l'emplacement)"
                            aria-label="Effacer"
                            onClick={() => handleDelete(v)}
                          />
                        </>
                      ) : (
                        <span className="text-secondary-light kkp-actions-hint">Double-clic pour remplir</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </KkpPlanningStickyTableWrap>
      )}
      pagination={pagination}
      onPageChange={(page) => loadVentes(page)}
      modals={(
        <>
          <KkpVenteSetModal
            show={showModal}
            onClose={closeModal}
            onSaved={() => loadVentes(pagination.page || 1)}
            onDeleted={() => loadVentes(pagination.page || 1)}
            onError={setError}
            meta={meta}
            commerciaux={commerciaux}
            defaultCommercialId={defaultCommercialId}
            vente={editingVente}
            initialValues={modalInitialValues}
            initialWizardStep={modalWizardStep}
            onRequestBatValide={(nextVente) => {
              closeModal();
              openBatValide(nextVente);
            }}
          />

          <KkpBatValideModal
            show={Boolean(batValideVente)}
            vente={batValideVente}
            onClose={closeBatValide}
            onSubmit={submitBatValide}
            submitting={batValideSubmitting}
            error={batValideError}
          />

          <KkpPlanningImportModal
            show={showImportModal}
            title="Importer le planning SET"
            sheetHint="La feuille VENTE SET du fichier sera lue automatiquement."
            canalLabel="SET"
            strategy={importStrategy}
            onStrategyChange={setImportStrategy}
            wipeScope={wipeScope}
            onWipeScopeChange={setWipeScope}
            periodLabel={importPeriodLabel}
            onClose={() => setShowImportModal(false)}
            onConfirm={handleImportConfirm}
          />
          {confirmModal}
        </>
      )}
    />
  );
};

export default KkpPlanningSetPage;
